/**
 * DecisionCard — the transcript's view of an assistant message that carries a
 * finite set of options.
 *
 * Rendered instead of the plain ChatBubble when an assistant message has an
 * ``options`` array. It shows the question; the waiting card's options are
 * answered in the composer, which owns the one answer road.
 *
 * - When ``isActive`` is false (i.e. the message is not the latest assistant
 *   message) and the card was never answered, its options are listed in a
 *   locked, non-interactive state so the user can read them.
 * - Once answered, only the question shows: the answer renders as the user's
 *   own bubble.
 */

import { chatLanguageDirection } from "@digital-twins/shared-types";

import { chatT } from "@/i18n";

import { cn } from "@/lib/utils";
import {
  builderCardText,
  builderOptionLabel,
  builderOptionMark,
} from "../utils/builderCardCopy";

interface DecisionCardOption {
  id?: string;
  label: string;
  value: string;
}

interface DecisionCardProps {
  /** AI avatar image src — matches the icon used by ChatBubble. */
  aiIconSrc: string;
  /** Whether this card is for the latest assistant message (unlocked). */
  isActive: boolean;
  message: {
    id: string;
    content: string;
    /** The language the card was written in (the chat's then). */
    language?: string;
    options: Array<DecisionCardOption>;
  };
  /** The opening-chain step that asked this card, from its record. */
  chainCode?: string | null;
  /**
   * True once the card's clarification is answered, or while the turn in
   * flight answers it (a local card once it is picked). The card then shows
   * its question only: the answer renders as the user's own bubble.
   */
  answered?: boolean;
}

/**
 * Renders message content line-by-line, ported from the private `ChatBubble`
 * in QuestionnaireBuilderChat.tsx: a `- `/`* `/`• ` prefixed line becomes a
 * bullet row with a hanging indent, a blank line becomes a small spacer, and
 * everything else is a plain block line.
 */
function renderContentLines(id: string, content: string) {
  return content.split("\n").map((line, index) => {
    const bulletMatch = /^[-*•]\s+(.*)$/.exec(line);
    if (bulletMatch) {
      return (
        <span key={`${id}-l${index}`} className="flex gap-2">
          <span aria-hidden="true" className="shrink-0">
            •
          </span>
          <span className="min-w-0">{bulletMatch[1]}</span>
        </span>
      );
    }
    if (line.length === 0) {
      return (
        <span
          key={`${id}-l${index}`}
          className="block h-2"
          aria-hidden="true"
        />
      );
    }
    return (
      <span key={`${id}-l${index}`} className="block">
        {line}
      </span>
    );
  });
}

export function DecisionCard(props: DecisionCardProps) {
  const { aiIconSrc, isActive, message, answered, chainCode } = props;

  const isAnswered = answered === true;

  return (
    <div className="flex gap-2 flex-row pr-[60px]">
      {/* AI avatar — identical to ChatBubble */}
      <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-full bg-study-chat-ai-bubble flex items-center justify-center">
        <img
          src={aiIconSrc}
          alt="AI"
          className="h-full w-full object-contain"
          style={{ transform: "scale(1.3)" }}
        />
      </div>

      <div
        className="flex-1 min-w-0 text-start"
        dir={chatLanguageDirection(message.language)}
      >
        {/* AI bubble — question text only */}
        <div
          data-testid="decision-card"
          className={cn(
            "rounded-2xl rounded-tl-sm px-4 pt-3 pb-3 text-base leading-[1.6]",
            "bg-study-chat-ai-bubble text-foreground",
          )}
        >
          {renderContentLines(
            message.id,
            builderCardText(message, chainCode, chatT(message.language)),
          )}
        </div>

        {/* Options — shown only for locked history that was never answered (the
            answer, once picked, renders as a user bubble). Active options live in
            the chat input area. */}
        {!isActive && !isAnswered && (
          <div className="mt-2 flex flex-col gap-1.5 opacity-70">
            {message.options.map((option, index) => (
              <div
                key={option.value}
                className="flex w-full items-center gap-2.5 rounded-lg border border-border/30 bg-background/60 px-3 py-2 text-sm opacity-50"
              >
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground">
                  {builderOptionMark(index)}
                </span>
                <span className="flex-1 text-foreground">
                  {builderOptionLabel(option, chainCode, chatT(message.language))}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
